import liveswitch from "fm.liveswitch";
import ChannelConnectionEvents from "./ChannelConnectionEvents";
import ConnectionData from "./ConnectionData";

interface Message {
    type: string;
    data: any;
}

export default class Messager {
    private channel: liveswitch.Channel;
    private events: ChannelConnectionEvents;
    private errorLogger: (err: string) => void;

    constructor(channel: liveswitch.Channel, events: ChannelConnectionEvents, errorLogger: (err: string) => void) {
        this.channel = channel;
        this.events = events;
        this.errorLogger = errorLogger;

        this.channel.addOnMessage(this.onMessage.bind(this));
        this.channel.addOnDeviceMessage(this.onDeviceMessage.bind(this));
    }

    public sendChatMessage(message: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.channel.sendMessage(message).then(() => {
                console.log("Sent message: " + message);
                resolve();
            }).fail(ex => {
                this.errorLogger(ex.message);
                reject();
            });
        });
    }

    private onMessage(clientInfo: liveswitch.ClientInfo, message: string) {
        try {
            const parsed = JSON.parse(message);
            const alias = parsed?.alias || clientInfo.getUserAlias();
            switch (parsed?.type) {
                case "raise_hand":
                    this.events.notify("onraisehand", { alias });
                    return;
                case "reaction":
                    this.events.notify("onreaction", { emoji: parsed.emoji, alias });
                    return;
                case "poll_create":
                    this.events.notify("onpoll", parsed.poll);
                    return;
                case "poll_vote":
                    this.events.notify("onpollvote", {
                        pollId: parsed.pollId,
                        optionIndex: parsed.optionIndex,
                        alias
                    });
                    return;
                case "poll_close":
                    this.events.notify("onpollclose", { pollId: parsed.pollId });
                    return;
                case "spotlight":
                    this.events.notify("onspotlight", { connectionId: parsed.connectionId ?? null });
                    return;
                case "mute_all":
                    this.events.notify("onmuteall", { fromAlias: parsed.fromAlias });
                    return;
                default:
                    break;
            }
        } catch {}
        this.events.notify("onmessage", { user: clientInfo.getUserAlias(), message });
    }

    private onDeviceMessage(client: liveswitch.ClientInfo, message: string) {
        try {
            const msg = JSON.parse(message) as Message;
            
            switch (msg.type) {
                case "request_control":
                    this.events.notify("onrequestcontrol", { fromId: client.getDeviceId(), fromAlias: client.getUserAlias(), data: msg.data });
                    break;
                case "camera_control_result":
                    this.events.notify("oncameracontrolresult", {
                        ...msg.data,
                        fromId: client.getDeviceId(),
                        fromAlias: client.getUserAlias(),
                    });
                    break;
                // We can add other message types here in the future
                default:
                    console.log(`Unknown message type received: ${msg.type}`);
            }
        } catch (e) {
            console.error("Could not process device message.", e);
        }
    }

    public requestScreenshot(connectionData: ConnectionData): Promise<VideoFrameData> {        
        console.log(`User ${this.channel.getUserId()} requested a screenshot from ${connectionData.userId}`);
        //this.channel.sendDeviceMessage(connectionData.userId, connectionData.deviceId, "screenshot");
        this.channel.sendUserMessage(connectionData.userId, "screenshot");

        return new Promise((resolve, reject) => {
            this.channel.addOnDeviceMessage((_, data: string) => {
                const videoFrameData = JSON.parse(data);
                resolve(videoFrameData);
            });
    
            setTimeout(() => {
                reject("Screenshot request timeout");
            }, 10000);
        });
    }

    private sendMessage(connectionData: ConnectionData, message: Message): liveswitch.Future<any> {
        return this.channel.sendDeviceMessage(connectionData.userId, connectionData.deviceId, JSON.stringify(message));
    }

    public sendImage(
        connectionData: ConnectionData,
        imageData: ImageAnnotationData
    ): liveswitch.Future<any> {
        const message: Message = {
            type: "image_data",
            data: imageData
        };
        return this.sendMessage(connectionData, message);
    }

    public sendControlRequest(connectionData: ConnectionData): liveswitch.Future<any> {
        const message: Message = {
            type: "request_control",
            data: {
                requestingUserAlias: this.channel.getUserAlias()
            }
        };
        return this.sendMessage(connectionData, message);
    }

    public sendCameraControl(
        connectionData: ConnectionData,
        action: "up" | "down" | "left" | "right" | "zoom_in" | "zoom_out" | "reset" | "stop" | "autofocus" | "manual_focus",
        options: { enabled?: boolean; value?: number } = {}
    ): liveswitch.Future<any> {
        const message: Message = {
            type: "camera_control",
            data: {
                action,
                ...options,
                requestingUserAlias: this.channel.getUserAlias(),
            },
        };
        return this.sendMessage(connectionData, message);
    }
}
